import { useEffect, useState } from 'react';
import { fetchMarketForecast } from '../api';
import { MarketForecast } from '../types';

// 🔮 장 예측 (격리 실험) — 오늘 코스피 종가 등락률 AI 점예측 + 나이브 베이스라인 대조 + 누적 승률.
//   ⚠️ 관찰 전용 — 매매·점수·알림 어디에도 안 쓰임(SCANNER_RULES #1 예외). 승률이 스스로를 증명할 때까지 구경만.
//   🎨 규격 V2(08-23): 보라 상자 은퇴 → 잉크 인셋. 방향색은 한국식(상승=웜/하락=쿨),
//      '미달·모자람'을 뜻하는 승률은 방향색이 아니라 회색(ink3).
const dirCls = (d?: string) => (d === '상승' ? 'text-dc-up' : d === '하락' ? 'text-dc-down' : 'text-dc-ink2');
const pctStr = (v: number | null | undefined) => (v == null ? '─' : `${v > 0 ? '+' : ''}${v}%`);
const winCls = (w?: number) => (w != null && w >= 55 ? 'text-dc-ok' : w != null && w < 45 ? 'text-dc-ink3' : 'text-dc-ink2');

function WinLine({ label, a, hi }: { label: string; a: { n: number; win?: number; mae?: number }; hi?: boolean }) {
  return (
    <div className={`flex items-center gap-2 text-[11.5px] py-1 px-1.5 ${hi ? 'bg-dc-bg2/60 rounded-md' : ''}`}>
      <span className="text-dc-ink2 w-24 shrink-0">{label}</span>
      <span className="text-dc-ink3 w-9">{a.n}일</span>
      {a.n > 0
        ? <><span className="font-mono">방향 <b className={winCls(a.win)}>{a.win}%</b></span>
            <span className="font-mono text-dc-ink3">오차 ±{a.mae}%p</span></>
        : <span className="text-dc-ink3">채점 대기</span>}
    </div>
  );
}

export default function ForecastCard() {
  const [data, setData] = useState<MarketForecast | null>(null);
  const [open, setOpen] = useState(false);
  const [reasonsOpen, setReasonsOpen] = useState(false);

  useEffect(() => { fetchMarketForecast().then(setData).catch(() => {}); }, []);
  if (!data) return null;
  const t = data.today, s = data.stats, ai = t.ai;

  return (
    <div className="rounded-lg border border-dc-line1 bg-dc-bg1/60 px-3 py-2.5">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex flex-wrap items-center gap-2 text-left">
        <span className="text-dc-ink1 text-[14px] font-[750]">장 예측 실험</span>
        {ai && (
          <span className="text-[11.5px] text-dc-ink2">오늘 코스피 <b className={dirCls(ai.direction)}>{ai.direction} {pctStr(ai.pct)}</b>
            <span className="text-dc-ink3"> · 신뢰 {ai.confidence ?? '─'}</span></span>
        )}
        {s.ai.n > 0 && <span className="ml-auto text-[10.5px] text-dc-ink3">누적 방향 {s.ai.win}% ({s.ai.n}일)</span>}
        <span className="text-dc-point/85 text-[10.5px]">{open ? '▾ 접기' : '▸ 펼치기'}</span>
      </button>

      <p className="text-[10.5px] text-dc-point/70 mt-1.5 leading-snug">관찰 전용 실험 — 매매 근거 아님. 예측이 얼마나 (안) 맞는지 데이터로 구경하는 곳(30일 전 결론 금지).</p>

      {open && (
        <div className="mt-3 space-y-3">
          {t.pending && <p className="text-dc-ink3 text-[11.5px]">{t.msg || '생성 대기'}</p>}
          {ai && (
            <div className="rounded-lg border border-dc-line1 bg-dc-bg1/60 p-2.5">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-[11.5px] text-dc-ink2">AI 점괘</span>
                <span className={`text-[13px] font-bold ${dirCls(ai.direction)}`}>{ai.direction} {pctStr(ai.pct)}</span>
                <span className="text-[10.5px] text-dc-ink3">신뢰 {ai.confidence}{ai._fallback ? ' · 폴백' : ''}</span>
                {t.scored && <span className={`ml-auto text-[11.5px] font-mono ${dirClsHit(ai.pct, t.actual_pct)}`}>실제 {pctStr(t.actual_pct)} {hitMark(ai.pct, t.actual_pct)}</span>}
              </div>
              {/* 🔮 '오늘 장 성격 예보' 줄은 2026-08-22에 뺐다 — 32일 채점 결과 적중 21.9%로
                   5지선다 찍기(20% 안팎)와 다를 바 없었다(같은 AI의 방향 예보는 75%). 서버도 이 질문을
                   프롬프트에서 지웠다(market_forecast.py 머리말). 옛 성적은 /market/forecast의
                   stats.weather에 박제로 남아 있고, 화면에서만 사라진 것이다. */}
              {ai.reasons && ai.reasons.length > 0 && (
                <>
                  <button onClick={() => setReasonsOpen((v) => !v)} className="text-[10.5px] text-dc-point/85 hover:text-dc-point mt-1.5">{reasonsOpen ? '▾ 접기' : '▸ 근거 보기'}</button>
                  {reasonsOpen && <ul className="mt-1 space-y-0.5">{ai.reasons.map((r, i) => <li key={i} className="text-[10.5px] text-dc-ink2 leading-snug">· {r}</li>)}</ul>}
                </>
              )}
            </div>
          )}

          {/* 대조군 — AI가 나이브 베이스라인을 이기나(이 실험의 진짜 질문) */}
          <div>
            <p className="text-[10.5px] font-[650] tracking-[.12em] text-dc-ink3 mb-1">누적 성적 — AI가 나이브 베이스라인을 이기나?</p>
            <WinLine label="AI" a={s.ai} hi />
            <WinLine label="① 전일 방향" a={s.baseline_momentum} />
            <WinLine label="② 미선물 방향" a={s.baseline_usfut} />
          </div>

          {Object.keys(s.by_confidence).length > 0 && (
            <div>
              <p className="text-[10.5px] font-[650] tracking-[.12em] text-dc-ink3 mb-1">신뢰도 캘리브레이션 — '높음'이 진짜 더 맞나?</p>
              {(['높음', '중간', '낮음'] as const).map((c) => s.by_confidence[c] && <WinLine key={c} label={`신뢰 ${c}`} a={s.by_confidence[c]} />)}
            </div>
          )}

          {s.recent.length > 0 && (
            <div>
              <p className="text-[10.5px] font-[650] tracking-[.12em] text-dc-ink3 mb-1">최근 채점</p>
              <div className="space-y-0.5">
                {s.recent.map((r) => (
                  <div key={r.date} className="flex items-center gap-2 text-[10.5px] font-mono">
                    <span className="text-dc-ink3 w-14">{r.date.slice(5)}</span>
                    <span className={dirCls(r.dir)}>{r.dir} {pctStr(r.pred)}</span>
                    <span className="text-dc-ink3">실제 {pctStr(r.actual)}</span>
                    <span className={r.hit ? 'text-dc-ok' : 'text-dc-ink3'}>{r.hit ? '✓' : '✗'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function dirClsHit(pred: number | undefined, actual: number | null | undefined) {
  if (pred == null || actual == null) return 'text-dc-ink2';
  return (pred > 0) === (actual > 0) ? 'text-dc-ok' : 'text-dc-ink3';
}
function hitMark(pred: number | undefined, actual: number | null | undefined) {
  if (pred == null || actual == null) return '';
  return (pred > 0) === (actual > 0) ? '✓' : '✗';
}
